import { useEffect, useState } from "react";
import { ShoppingBag, Search, Package, MapPin, User, Phone, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import usePageTitle from "@/hooks/use-page-title";
import { fmtDate, fmtDateTime } from "@/lib/date";

const STATUS_OPTIONS = [
  { v: "payment_pending", label: "Payment Pending", cls: "bg-amber-100 text-amber-800" },
  { v: "paid", label: "Paid", cls: "bg-emerald-100 text-emerald-700" },
  { v: "processing", label: "Processing", cls: "bg-blue-100 text-blue-800" },
  { v: "shipped", label: "Shipped", cls: "bg-indigo-100 text-indigo-800" },
  { v: "delivered", label: "Delivered", cls: "bg-emerald-200 text-emerald-900" },
  { v: "cancelled", label: "Cancelled", cls: "bg-zinc-200 text-zinc-600" },
  { v: "refunded", label: "Refunded", cls: "bg-red-100 text-red-700" },
];
const statusLabel = (v) => STATUS_OPTIONS.find((s) => s.v === v) || { label: v, cls: "bg-zinc-100" };

export default function ShopOrders() {
  usePageTitle("Shop Orders");
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try { setRows((await api.get("/shop/orders")).data); }
    catch { toast.error("Could not load shop orders"); }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((o) =>
    (!statusFilter || o.status === statusFilter) &&
    (!q || `${o.order_no} ${o.address?.full_name || ""} ${o.address?.phone || ""}`.toLowerCase().includes(q.toLowerCase()))
  );

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/shop/orders/${id}/status`, { status });
      toast.success(`Marked ${statusLabel(status).label}`);
      load();
      if (selected?.id === id) {
        setSelected({ ...selected, status });
      }
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ShoppingBag className="text-[#FF6B00]" />
        <h1 className="text-2xl font-black tracking-tight">Shop Orders</h1>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[240px] flex items-center border-2 border-zinc-200 rounded-lg px-3 py-2 bg-white">
          <Search size={14} className="text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order no, name, phone…"
            className="flex-1 px-2 bg-transparent outline-none text-sm" data-testid="shop-order-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input max-w-xs" data-testid="shop-status-filter">
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
      </div>

      <div className="border-2 border-zinc-200 rounded-2xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">Customer</th>
                <th className="text-left px-4 py-3">Items</th>
                <th className="text-left px-4 py-3">Total</th>
                <th className="text-left px-4 py-3">Payment</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="shop-orders-rows">
              {filtered.map((o) => {
                const s = statusLabel(o.status);
                return (
                  <tr key={o.id} className="border-t border-zinc-100 hover:bg-zinc-50">
                    <td className="px-4 py-3 font-mono font-bold">{o.order_no}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{o.address?.full_name}</div>
                      <div className="text-xs text-zinc-500">{o.address?.phone}</div>
                    </td>
                    <td className="px-4 py-3">{o.items?.length || 0}</td>
                    <td className="px-4 py-3 font-mono font-bold">₹{Math.round(o.total).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs">{o.payment_method?.toUpperCase()}</span>
                      <div className={`text-[10px] font-bold ${o.payment_status === "paid" ? "text-emerald-700" : "text-amber-700"}`}>{o.payment_status?.toUpperCase()}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${s.cls}`}>{s.label}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500">{fmtDateTime(o.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setSelected(o)} className="text-xs font-bold text-[#FF6B00]" data-testid={`view-shop-order-${o.id}`}>View</button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-zinc-500">No shop orders yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="shop-order-drawer">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline text-[#FF6B00]">Order</div>
                <div className="font-mono font-black text-xl">{selected.order_no}</div>
                <div className="text-xs text-zinc-500">{fmtDateTime(selected.created_at)}</div>
              </div>
              <button onClick={() => setSelected(null)}><X size={20} /></button>
            </div>

            {/* Status changer */}
            <div className="mb-4">
              <div className="text-[10px] uppercase font-bold text-zinc-500 mb-1">Update Status</div>
              <select value={selected.status} onChange={(e) => updateStatus(selected.id, e.target.value)} className="input" data-testid="shop-order-status-select">
                {STATUS_OPTIONS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
            </div>

            <div className="border-2 border-zinc-200 rounded-xl p-4 mb-4">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-zinc-500 mb-2"><User size={12} /> Customer</div>
              <div className="font-bold">{selected.address?.full_name}</div>
              <div className="text-sm text-zinc-600 flex items-center gap-2"><Phone size={12} /> {selected.address?.phone}</div>
              {selected.address?.email && <div className="text-sm text-zinc-600">✉️ {selected.address.email}</div>}
            </div>

            <div className="border-2 border-zinc-200 rounded-xl p-4 mb-4">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-zinc-500 mb-2"><MapPin size={12} /> Address</div>
              <div className="text-sm">
                {selected.address?.line1}{selected.address?.line2 ? `, ${selected.address.line2}` : ""}<br />
                {selected.address?.city}, {selected.address?.state} — {selected.address?.pincode}
                {selected.address?.landmark && <div className="text-zinc-500 text-xs">Landmark: {selected.address.landmark}</div>}
              </div>
            </div>

            <div className="border-2 border-zinc-200 rounded-xl p-4 mb-4">
              <div className="inline-flex items-center gap-2 text-xs font-bold text-zinc-500 mb-2"><Package size={12} /> Items</div>
              <div className="space-y-2 text-sm">
                {selected.items?.map((it) => (
                  <div key={it.product_id} className="flex justify-between border-b border-zinc-100 pb-1 last:border-0">
                    <div>
                      <div className="font-semibold">{it.name}</div>
                      <div className="text-xs text-zinc-500">Qty {it.quantity}</div>
                    </div>
                    <div className="font-mono font-bold">₹{Math.round(it.line_total).toLocaleString("en-IN")}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-2 border-zinc-200 rounded-xl p-4 mb-4 text-sm space-y-1">
              <Row label="Subtotal" val={selected.subtotal} />
              {selected.coupon_discount > 0 && <Row label={`Coupon (${selected.coupon_code})`} val={-selected.coupon_discount} accent="text-emerald-700" />}
              {selected.gift_card_applied > 0 && <Row label={`Gift Card (${selected.gift_card_code})`} val={-selected.gift_card_applied} accent="text-emerald-700" />}
              <Row label="Shipping" val={selected.shipping} />
              <div className="border-t border-zinc-200 pt-2 mt-2 flex justify-between text-lg">
                <span className="font-bold">Total</span>
                <span className="font-mono font-black text-[#FF6B00]">₹{Math.round(selected.total).toLocaleString("en-IN")}</span>
              </div>
            </div>

            {selected.notes && (
              <div className="border-2 border-zinc-200 rounded-xl p-4 text-sm">
                <div className="text-[10px] uppercase font-bold text-zinc-500 mb-1">Customer Notes</div>
                {selected.notes}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, val, accent = "" }) {
  return (
    <div className={`flex justify-between ${accent}`}>
      <span className="text-zinc-600">{label}</span>
      <span className="font-mono font-bold">{val < 0 ? "−" : ""}₹{Math.round(Math.abs(val)).toLocaleString("en-IN")}</span>
    </div>
  );
}
